import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import LoginPage from "./LoginPage/LoginPage";
import OrgProfileForm from "./LoginPage/OrgProfileForm";
import Dashboard from "./Dashboard/Dashboard";
import DashboardHome from "./Dashboard/DashboardHome";
import InvoicePage from "./Sales/InvoicePage";
import ViewInvoice from "./Sales/ViewInvoice";
import SalesRegister from "./Reports/SalesRegister";
import AddItemModal from "./Inventory/AddItemModal";
import EditInvoice from "./Sales/EditInvoice";
import ViewInventory from "./Inventory/ViewInventory";
import EditInventory from "./Inventory/EditInventory";
import ViewInvoiceDetails from "./Sales/ViewInvoiceDetails";
import OrgDetailsView from "./Admin/OrgDetailsPopup";
import CustomerList from "./Admin/viewUserDetails";
import EditOrgProfileForm from "./Admin/editOrgDetails";
import GenerateReceipt from "./Receipt/GenerateReceipt";
import ViewInventoryDetails from "./Inventory/ViewInventryDetails";
import QuickInvoice from "./Sales/QuickInvoice";
import PerformaInvoice from "./Sales/PerformaInvoice";
import ViewReceipt from "./Receipt/ViewReceipt";
import CategoryManagement from "./Master/CategoryManagement";
import BrandManagement from "./Master/BrandManagement";
import ManufacturerManagement from "./Master/ManufacturerManagement";
import SalesByItemReport from "./Reports/SalesByItemReport";
import CategoryWiseStockReport from "./Reports/CategoryWiseStockReport";
import AuthHandler from "./AuthHandler";
import RapidQuickInvoice from "./Sales/RapidQuickInvoice";
import ChangePasswordScreen from "./Admin/ChangePasswordScreen";
import ForgotPassword from "./LoginPage/ForgotPassword";
import ResetPassword from "./LoginPage/ResetPassword";
import PurchaseOrderForm from "./Sales/PurchaseOrder/PurchaseOrderForm";
import ViewPurchaseOrders from "./Sales/PurchaseOrder/ViewPurchaseOrders";
import ViewPODetails from "./Sales/PurchaseOrder/ViewPODetails";
import AddWarehouseForm from "./Master/AddWarehouseForm";
import EditPurchaseOrder from "./Sales/PurchaseOrder/EditPurchaseOrder";
import CreateCreditNote from "./CreditDebitNote/CreateCreditNote";
import CreateDebitNote from "./CreditDebitNote/CreateDebitNote";
import {
  clearSession,
  getSession,
  isSessionExpired,
} from "../src/utils/Session";
import StockManagement from "./Inventory/stockManagement/StockManagement";
import { SnackbarProvider } from "notistack";
import ViewLedger from "./Ledger/ViewLedger";
import B2BSalesRegisterGST from "./Reports/B2BSalesRegisterGST";

// ── NEW: subscription system ────────────────────────────────────────────────
import { SubscriptionProvider } from "./Hooks/useSubscription";
import ProtectedFeatureRoute from "./Route/protectedfeatureroute";
import SubscriptionRequiredPage from "./Subscriptions/SubscriptionRequiredPage";

function App() {
  const [user, setUser] = useState(() => {
    const session = getSession();
    return session && !isSessionExpired(session);
  });
  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={3000}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {/* ── NEW: wraps the whole app so useSubscription() works everywhere,
          including on the login/public routes (it simply won't fetch
          anything meaningful until the user is authenticated). ──────────── */}
      <SubscriptionProvider>
        <Router>
          <AuthHandler setUser={setUser} />
          <Routes>
            <Route path="/" element={<LoginPage setUser={setUser} />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/OrgProfileForm" element={<OrgProfileForm />} />
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/" replace />}
            >
              <Route index element={<DashboardHome />} />

              {/* ── NEW: fallback page for locked modules ──────────────── */}
              <Route
                path="subscription-required"
                element={<SubscriptionRequiredPage />}
              />

              {/* Sales — always available on every seeded plan (invoice.enabled) */}
              <Route path="sales/new-invoice" element={<InvoicePage />} />
              <Route path="sales/view-invoice" element={<ViewInvoice />} />
              <Route
                path="sales/rapid-Quick-invoice"
                element={<RapidQuickInvoice />}
              />
              <Route path="sales/quick-invoice" element={<QuickInvoice />} />
              <Route
                path="sales/performa-invoice"
                element={<PerformaInvoice />}
              />
              <Route
                path="sales/view-invoice-details"
                element={<ViewInvoiceDetails />}
              />
              <Route path="sales/credit-note" element={<CreateCreditNote />} />
              <Route path="sales/debit-note" element={<CreateDebitNote />} />
              <Route path="sales/edit-invoice" element={<EditInvoice />} />

              {/* Reports — gated by "reports" feature */}
              <Route
                path="report/sales-report"
                element={
                  <ProtectedFeatureRoute feature="reports">
                    <SalesRegister />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="report/b2b-Sales-report"
                element={
                  <ProtectedFeatureRoute feature="reports">
                    <B2BSalesRegisterGST />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="report/sales-report-items"
                element={
                  <ProtectedFeatureRoute feature="reports">
                    <SalesByItemReport />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="report/item-report-categoryWise-stock"
                element={
                  <ProtectedFeatureRoute feature="reports">
                    <CategoryWiseStockReport />
                  </ProtectedFeatureRoute>
                }
              />

              {/* Purchase — gated by "purchaseOrder" feature */}
              <Route
                path="purchase/purchase-order-form"
                element={
                  <ProtectedFeatureRoute feature="purchaseOrder">
                    <PurchaseOrderForm />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="purchase/view-purchase-orders"
                element={
                  <ProtectedFeatureRoute feature="purchaseOrder">
                    <ViewPurchaseOrders />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="purchase/view-po-orders"
                element={
                  <ProtectedFeatureRoute feature="purchaseOrder">
                    <ViewPODetails />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="purchase/edit-po"
                element={
                  <ProtectedFeatureRoute feature="purchaseOrder">
                    <EditPurchaseOrder />
                  </ProtectedFeatureRoute>
                }
              />

              {/* Inventory — gated by "inventory" feature */}
              <Route
                path="inventory/add-new-item"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <AddItemModal />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="inventory/view-inventory"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <ViewInventory />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="inventory/stock-management"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <StockManagement />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="inventory/edit-inventory"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <EditInventory />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="inventory/view-item-details"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <ViewInventoryDetails />
                  </ProtectedFeatureRoute>
                }
              />

              {/* Master — category/brand/manufacturer/warehouse feed
                  Inventory, so gated by the same "inventory" feature.
                  Change this to its own feature key later if you ever want
                  Master to be independently priced/plan-gated. */}
              <Route
                path="Master/inventory/add-category"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <CategoryManagement />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="Master/inventory/add-brand"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <BrandManagement />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="Master/inventory/add-manufacturer"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <ManufacturerManagement />
                  </ProtectedFeatureRoute>
                }
              />
              <Route
                path="Master/inventory/add-warehouse"
                element={
                  <ProtectedFeatureRoute feature="inventory">
                    <AddWarehouseForm />
                  </ProtectedFeatureRoute>
                }
              />

              {/* Admin, Receipt, Ledger — not modeled as plan features today
                  (every plan in the seed data allows them). Wrap with
                  <ProtectedFeatureRoute feature="..."> the same way as above
                  the moment you want to gate any of these by plan — just add
                  the matching key under `features` on the plan document(s)
                  first. */}
              <Route path="Admin/view-org" element={<OrgDetailsView />} />
              <Route path="Admin/edit-org" element={<EditOrgProfileForm />} />
              <Route path="Admin/view-user" element={<CustomerList />} />
              <Route
                path="Admin/change-password"
                element={<ChangePasswordScreen />}
              />
              <Route
                path="/dashboard/receipt/generate-receipt"
                element={<GenerateReceipt />}
              />
              <Route
                path="/dashboard/sales/view-receipt"
                element={<ViewReceipt />}
              />
              <Route
                path="/dashboard/ledger/customer-ledger-view"
                element={<ViewLedger />}
              />
            </Route>
          </Routes>
        </Router>
      </SubscriptionProvider>
    </SnackbarProvider>
  );
}
export default App;

// import React, { useState } from "react";
// import {
//   BrowserRouter as Router,
//   Route,
//   Routes,
//   Navigate,
// } from "react-router-dom";
// import LoginPage from "./LoginPage/LoginPage";
// import OrgProfileForm from "./LoginPage/OrgProfileForm";
// import Dashboard from "./Dashboard/Dashboard";
// import DashboardHome from "./Dashboard/DashboardHome";
// import InvoicePage from "./Sales/InvoicePage";
// import ViewInvoice from "./Sales/ViewInvoice";
// import SalesRegister from "./Reports/SalesRegister";
// import AddItemModal from "./Inventory/AddItemModal";
// import EditInvoice from "./Sales/EditInvoice";
// import ViewInventory from "./Inventory/ViewInventory";
// import EditInventory from "./Inventory/EditInventory";
// import ViewInvoiceDetails from "./Sales/ViewInvoiceDetails";
// import OrgDetailsView from "./Admin/OrgDetailsPopup";
// import CustomerList from "./Admin/viewUserDetails";
// import EditOrgProfileForm from "./Admin/editOrgDetails";
// // import PendingInvoices from "./Receipt/PendingInvoices";
// // import PaymentPage from "./Receipt/PaymentPage";
// import GenerateReceipt from "./Receipt/GenerateReceipt";
// //import ReceiptPage from "./Receipt/ReceiptPage";
// import ViewInventoryDetails from "./Inventory/ViewInventryDetails";
// import QuickInvoice from "./Sales/QuickInvoice";
// import PerformaInvoice from "./Sales/PerformaInvoice";
// import ViewReceipt from "./Receipt/ViewReceipt";
// import CategoryManagement from "./Master/CategoryManagement";
// import BrandManagement from "./Master/BrandManagement";
// import ManufacturerManagement from "./Master/ManufacturerManagement";
// import SalesByItemReport from "./Reports/SalesByItemReport";
// import CategoryWiseStockReport from "./Reports/CategoryWiseStockReport";
// import AuthHandler from "./AuthHandler";
// import RapidQuickInvoice from "./Sales/RapidQuickInvoice";
// import ChangePasswordScreen from "./Admin/ChangePasswordScreen";
// import ForgotPassword from "./LoginPage/ForgotPassword";
// import ResetPassword from "./LoginPage/ResetPassword";
// import PurchaseOrderForm from "./Sales/PurchaseOrder/PurchaseOrderForm";
// import ViewPurchaseOrders from "./Sales/PurchaseOrder/ViewPurchaseOrders";
// import ViewPODetails from "./Sales/PurchaseOrder/ViewPODetails";
// import AddWarehouseForm from "./Master/AddWarehouseForm";
// import EditPurchaseOrder from "./Sales/PurchaseOrder/EditPurchaseOrder";
// import CreateCreditNote from "./CreditDebitNote/CreateCreditNote";
// import CreateDebitNote from "./CreditDebitNote/CreateDebitNote";
// import {
//   clearSession,
//   getSession,
//   isSessionExpired,
// } from "../src/utils/Session";
// import StockManagement from "./Inventory/stockManagement/StockManagement";
// import { SnackbarProvider } from "notistack";
// import ViewLedger from "./Ledger/ViewLedger";
// import B2BSalesRegisterGST from "./Reports/B2BSalesRegisterGST";

// function App() {
//   const [user, setUser] = useState(() => {
//     const session = getSession();
//     return session && !isSessionExpired(session);
//     // return localStorage.getItem("token") ? true : false;
//   });
//   return (
//     <SnackbarProvider
//       maxSnack={3}
//       autoHideDuration={3000}
//       anchorOrigin={{ vertical: "top", horizontal: "right" }}
//     >
//       <Router>
//         <AuthHandler setUser={setUser} /> 
//         <Routes>
//           <Route path="/" element={<LoginPage setUser={setUser} />} />
//           <Route path="/reset-password" element={<ResetPassword />} />
//           {/* Added by Gaurav Singh */}
//           <Route path="/forgot-password" element={<ForgotPassword />} />
//           <Route path="/OrgProfileForm" element={<OrgProfileForm />} />
//           <Route
//             path="/dashboard"
//             element={user ? <Dashboard /> : <Navigate to="/" replace />}
//           >
//             <Route index element={<DashboardHome />} />
//             <Route path="sales/new-invoice" element={<InvoicePage />} />
//             <Route path="sales/view-invoice" element={<ViewInvoice />} />
//             <Route
//               path="sales/rapid-Quick-invoice"
//               element={<RapidQuickInvoice />}
//             />
//             <Route path="sales/quick-invoice" element={<QuickInvoice />} />
//             <Route
//               path="sales/performa-invoice"
//               element={<PerformaInvoice />}
//             />
//             <Route
//               path="sales/view-invoice-details"
//               element={<ViewInvoiceDetails />}
//             />
//             <Route
//               path="sales/credit-note"
//               element={<CreateCreditNote />}
//             />
//             <Route
//               path="sales/debit-note"
//               element={<CreateDebitNote />}
//             />
//             <Route path="report/sales-report" element={<SalesRegister />} />
//             <Route path="report/b2b-Sales-report" element={<B2BSalesRegisterGST />} />
            
//             <Route
//               path="report/sales-report-items"
//               element={<SalesByItemReport />}
//             />
//             <Route
//               path="report/item-report-categoryWise-stock"
//               element={<CategoryWiseStockReport />}
//             />
//             <Route
//               path="purchase/purchase-order-form"
//               element={<PurchaseOrderForm/>}
//             />
//              <Route
//               path="purchase/view-purchase-orders"
//               element={<ViewPurchaseOrders/>}
//             />
//              <Route
//               path="purchase/view-po-orders"
//               element={<ViewPODetails/>}
//             />
//             <Route
//               path="purchase/edit-po"
//               element={<EditPurchaseOrder/>}
//             />
//             <Route path="inventory/add-new-item" element={<AddItemModal />} />
//             <Route
//               path="inventory/view-inventory"
//               element={<ViewInventory />}
//             />

//             <Route
//               path="inventory/stock-management"
//               element={<StockManagement />}
//             />

//             <Route path="sales/edit-invoice" element={<EditInvoice />} />
//             <Route
//               path="inventory/edit-inventory"
//               element={<EditInventory />}
//             />
//             <Route
//               path="inventory/view-item-details"
//               element={<ViewInventoryDetails />}
//             />
//             <Route path="Admin/view-org" element={<OrgDetailsView />} />
//             <Route path="Admin/edit-org" element={<EditOrgProfileForm />} />
//             <Route path="Admin/view-user" element={<CustomerList />} />
//             <Route
//               path="Admin/change-password"
//               element={<ChangePasswordScreen />}
//             />
//             {/* <Route
//               path="/dashboard/sales/generate-receipt"
//               element={<PendingInvoices />}
//             /> */}
//             <Route
//               path="/dashboard/receipt/generate-receipt"
//               element={<GenerateReceipt/>}
//             />
//             {/* <Route
//               path="/dashboard/sales/receipt/:invoiceId"
//               element={<ReceiptPage />}
//             /> */}
//             <Route
//               path="/dashboard/sales/view-receipt"
//               element={<ViewReceipt />}
//             />
//             <Route
//               path="Master/inventory/add-category"
//               element={<CategoryManagement />}
//             />
//             <Route
//               path="Master/inventory/add-brand"
//               element={<BrandManagement />}
//             />
//             <Route
//               path="Master/inventory/add-manufacturer"
//               element={<ManufacturerManagement />}
//             />
//              <Route
//               path="Master/inventory/add-warehouse"
//               element={<AddWarehouseForm />}
//             />
//             <Route
//               path="/dashboard/ledger/customer-ledger-view"
//               element={<ViewLedger />}
//             />
//             {/* </Route> */}
//           </Route>
//         </Routes>
//       </Router>
//     </SnackbarProvider>
//   );
// }
// export default App;

// import React, { useState } from "react";
// import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
// import LoginPage from './LoginPage/LoginPage';
// import OrgProfileForm from './LoginPage/OrgProfileForm';
// import Dashboard from "./Dashboard/Dashboard";
// import DashboardHome from "./Dashboard/DashboardHome";
// import InvoicePage from "./Sales/InvoicePage";
// import ViewInvoice from "./Sales/ViewInvoice";
// import SalesReport from "./Reports/SalesReport";
// import AddItemModal from "./Inventory/AddItemModal";
// import EditInvoice from "./Sales/EditInvoice";
// import ViewInventory from './Inventory/ViewInventory';
// import EditInventory from './Inventory/EditInventory';
// import ViewInvoiceDetails from './Sales/ViewInvoiceDetails';
// import OrgDetailsView from './Admin/OrgDetailsPopup';
// import CustomerList from './Admin/viewUserDetails';
// import EditOrgProfileForm from './Admin/editOrgDetails';
// import PendingInvoices from "./Receipt/PendingInvoices";
// import PaymentPage from "./Receipt/PaymentPage";
// import ReceiptPage from "./Receipt/ReceiptPage";
// import ViewInventoryDetails from './Inventory/ViewInventryDetails';
// import QuickInvoice from './Sales/QuickInvoice';
// import PerformaInvoice from './Sales/PerformaInvoice';
// import ViewReceipt from "./Receipt/ViewReceipt";
// import CategoryManagement from "./Master/CategoryManagement";
// import BrandManagement from './Master/BrandManagement';
// import ManufacturerManagement from "./Master/ManufacturerManagement";
// import SalesByItemReport from "./Reports/SalesByItemReport";
// import CategoryWiseStockReport from "./Reports/CategoryWiseStockReport";
// import ProtectedRoute from "./ProtectedRoute";
// import AuthHandler from "./AuthHandler"; // ✅ Import the fixed AuthHandler
// import DebugRender from "./DebugRender";

// function App() {
//   const [user, setUser] = useState(() => {
//     return localStorage.getItem("token") ? true : false;
//   });
//   return (
//     <Router>
//       <DebugRender name="App" />
//       <AuthHandler setUser={setUser} /> {/* ✅ Runs once inside Router */}
//       <Routes>
//       <Route path="/" element={<LoginPage setUser={setUser} />} />
//         <Route path="/OrgProfileForm" element={<OrgProfileForm />} />
//         <Route
//           path="/dashboard"
//           element={user ? <Dashboard /> : <Navigate to="/" replace />}
//         >
//           <Route index element={<DashboardHome />} />
//           <Route path="sales/new-invoice" element={<InvoicePage />} />
//           <Route path="sales/view-invoice" element={<ViewInvoice />} />
//           <Route path="sales/quick-invoice" element={<QuickInvoice />} />
//           <Route path="sales/performa-invoice" element={<PerformaInvoice />} />
//           <Route path="sales/view-invoice-details" element={<ViewInvoiceDetails />} />
//           <Route path="report/sales-report" element={<SalesReport />} />
//           <Route path="report/sales-report-items" element={<SalesByItemReport />} />
//           <Route path="report/item-report-categoryWise-stock" element={<CategoryWiseStockReport />} />
//           <Route path="inventory/add-new-item" element={<AddItemModal />} />
//           <Route path="inventory/view-inventory" element={<ViewInventory />} />
//           <Route path="sales/edit-invoice" element={<EditInvoice />} />
//           <Route path="inventory/edit-inventory" element={<EditInventory />} />
//           <Route path="inventory/view-item-details" element={<ViewInventoryDetails />} />
//           <Route path="Admin/view-org" element={<OrgDetailsView />} />
//           <Route path="Admin/edit-org" element={<EditOrgProfileForm />} />
//           <Route path="Admin/view-user" element={<CustomerList />} />
//           <Route path="/dashboard/sales/generate-receipt" element={<PendingInvoices />} />
//           <Route path="/dashboard/sales/generate-receipt/payment/:invoiceId" element={<PaymentPage />} />
//           <Route path="/dashboard/sales/receipt/:invoiceId" element={<ReceiptPage />} />
//           <Route path="/dashboard/sales/view-receipt" element={<ViewReceipt />} />
//           <Route path="Master/inventory/add-category" element={<CategoryManagement />} />
//           <Route path="Master/inventory/add-brand" element={<BrandManagement />} />
//           <Route path="Master/inventory/add-manufacturer" element={<ManufacturerManagement />} />
//         </Route>
//       </Routes>
//     </Router>
//   );
// }

// export default App;
